import { Component, forwardRef, Input, OnChanges, OnInit, SimpleChanges, OnDestroy } from '@angular/core';
import {
  ChargePeriodScheduleEntry,
  ChargeScheduleEntry,
  NetworkProviderPlan,
  NetworkProviderPlanPeriodType
} from '../../../services/api/access-sites.interface';
import {getCurrencySymbol} from '@angular/common';
import {
  AbstractControl,
  ControlValueAccessor,
  UntypedFormArray,
  UntypedFormControl,
  UntypedFormGroup,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
  Validator
} from '@angular/forms';
import {NgbDateAdapter, NgbDateStruct} from '@ng-bootstrap/ng-bootstrap';
import {Moment} from 'moment-timezone';
import {MomentNgbDateAdapter} from '../../../util/ng-bootstrap/moment-ngb-date-adapter';
import {CalendarUtilsService} from '../../../services';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-add-edit-network-provider-plan-charge',
  templateUrl: './add-edit-network-provider-plan-charge.component.html',
  styleUrls: ['./add-edit-network-provider-plan-charge.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AddEditNetworkProviderPlanChargeComponent),
      multi: true
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => AddEditNetworkProviderPlanChargeComponent),
      multi: true
    }
  ],
  standalone: false
})
export class AddEditNetworkProviderPlanChargeComponent implements OnInit, OnDestroy, OnChanges, ControlValueAccessor, Validator {

  @Input()
  networkProviderPlan: NetworkProviderPlan;
  @Input()
  showStartTs = true;
  @Input()
  timezone: string;
  @Input()
  maxDate: Moment;
  @Input()
  minDate: Moment;
  @Input()
  excludeDates: Moment[];

  monthList: Array<{ id: number, name: string }>;

  minDateStruct: NgbDateStruct;
  maxDateStruct: NgbDateStruct;
  formGroup: UntypedFormGroup;
  networkProviderPlanPeriodTypes: NetworkProviderPlanPeriodType[];

  readonly CONTROL_KEY_START_MONTH = 'startMonth';
  readonly CONTROL_KEY_END_MONTH = 'endMonth';
  readonly CONTROL_KEY_START_TS = 'startTs';
  readonly CONTROL_KEY_CHARGE_PERIODS = 'chargePeriods';
  readonly CONTROL_KEY_CHARGES = 'charges';
  private readonly SET_VALUE_OPTIONS = {emitEvent: false};

  private ngbDateAdapter: NgbDateAdapter<Moment>;
  private ngUnsubscribe = new Subject<void>();

  private onChange: (_) => void = (_) => {};
  private onTouched: () => void = () => {};
  private validatorChange: () => void = () => {};

  constructor(private calendarUtilsService: CalendarUtilsService) { }

  ngOnInit() {
    this.monthList = this.calendarUtilsService.monthList;

    this.ngbDateAdapter = new MomentNgbDateAdapter(this.timezone);

    this.networkProviderPlanPeriodTypes = [];
    const m = new Map<number, NetworkProviderPlanPeriodType>();
    for (const entry of this.networkProviderPlan.entries) {
      m.set(entry.type.network_provider_plan_period_type_id, entry.type);
    }
    this.networkProviderPlanPeriodTypes = Array.from(m.values()).sort((a, b) => a.network_provider_plan_period_type_id - b.network_provider_plan_period_type_id);


    this.formGroup = new UntypedFormGroup(
      {
        startTs: new UntypedFormControl(),
        chargePeriods: new UntypedFormArray([])
      });

    this.addChargePeriod();

    this.formGroup.valueChanges.pipe(takeUntil(this.ngUnsubscribe)).subscribe(this.publishChange);

    if (this.minDate) {
      this.minDateStruct = this.ngbDateAdapter.fromModel(this.minDate);
    }
    if (this.maxDate) {
      this.maxDateStruct = this.ngbDateAdapter.fromModel(this.maxDate);
    }
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.minDate || changes.maxDate || changes.excludeDates) {
      this.validatorChange();
    }
  }

  getStartTsControl(): UntypedFormControl {
    return <UntypedFormControl>this.formGroup.get(this.CONTROL_KEY_START_TS);
  }

  getChargePeriodControls(): UntypedFormArray {
    return <UntypedFormArray>this.formGroup.get(this.CONTROL_KEY_CHARGE_PERIODS);
  }

  getChargeControls(fg: UntypedFormGroup): UntypedFormArray {
    return <UntypedFormArray>fg.get(this.CONTROL_KEY_CHARGES);
  }

  retrieveCurrencySymbol(code: string): string {
    return getCurrencySymbol(code, 'narrow');
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    if (isDisabled) {
      this.formGroup.disable();
    } else {
      this.formGroup.enable();
    }
  }

  writeValue(value: ChargeScheduleEntry): void {
    this.getStartTsControl().setValue(value ? this.ngbDateAdapter.fromModel(value.start_ts) : null, this.SET_VALUE_OPTIONS);

    const chargePeriodControls = this.getChargePeriodControls();
    while (chargePeriodControls.controls.length) {
      chargePeriodControls.removeAt(0);
    }

    if (value && value.chargePeriods && value.chargePeriods.length) {
      value.chargePeriods.forEach(chargePeriod => {
        this.addChargePeriod(chargePeriod);
      });
    } else {
      this.addChargePeriod();
    }
  }

  registerOnValidatorChange(fn: () => void): void {
    this.validatorChange = fn;
  }

  validate(control: AbstractControl): ValidationErrors | null {
    const errors: ValidationErrors = {};

    const startTsControl =  this.getStartTsControl();
    const startTsErrors = startTsControl.errors;
    if (startTsErrors) {
      errors[this.CONTROL_KEY_START_TS] = startTsErrors;
    } else if (this.excludeDates && this.excludeDates.some(m => m ? m.isSame(this.ngbDateAdapter.toModel(startTsControl.value)) : false)) {
      const error = {invalid: {messageKey: 'Date not allowed'}};
      startTsControl.setErrors(error);
      errors[this.CONTROL_KEY_START_TS] = error;
    }

    const chargeErrors = {};

    const chargePeriods = this.getChargePeriodControls();
    let prevEndMonthControlValue: number;
    for (let i = 0; i < chargePeriods.controls.length; i++) {
      const chargePeriodFG = <UntypedFormGroup>chargePeriods.controls[i];

      const startMonthControl = chargePeriodFG.get(this.CONTROL_KEY_START_MONTH);
      if (startMonthControl.value !== undefined && startMonthControl.value !== null) {
        if (i === 0 && startMonthControl.value !== 0) {
          startMonthControl.setErrors({startWithZero: {messageKey: 'Need to start with January'}});
        } else if (prevEndMonthControlValue !== undefined && prevEndMonthControlValue !== null) {
          const diff = startMonthControl.value - prevEndMonthControlValue;
          if (diff < 1) {
            startMonthControl.setErrors({overlap: {messageKey: 'Overlap'}});
          } else if (diff > 1) {
            startMonthControl.setErrors({gap: {messageKey: 'Gap'}});
          }
        } else {
          startMonthControl.setErrors(null);
        }
      }

      const startMonthErrors = startMonthControl.errors;
      if (startMonthErrors) {
        chargeErrors[this.CONTROL_KEY_CHARGES + '-' + i + '-' + this.CONTROL_KEY_START_MONTH] = startMonthErrors;
      }

      const endMonthControl = chargePeriodFG.get(this.CONTROL_KEY_END_MONTH);
      if (endMonthControl.value !== undefined && endMonthControl.value !== null) {
        if (i === chargePeriods.length - 1 && endMonthControl.value !== 11) {
          endMonthControl.setErrors({startWithZero: {messageKey: 'Need to cover the full year'}});
        } else {
          endMonthControl.setErrors(null);
        }
      }

      prevEndMonthControlValue = endMonthControl.value;

      const endMonthErrors = endMonthControl.errors;
      if (endMonthErrors) {
        chargeErrors[this.CONTROL_KEY_CHARGES + '-' + i + '-' + this.CONTROL_KEY_END_MONTH] = endMonthErrors;
      }

      const chargeControls = this.getChargeControls(chargePeriodFG).controls;
      for (let j = 0; j < chargeControls.length; j++) {
        const chargeControl = chargeControls[j];
        if (chargeControl.errors) {
          chargeErrors[this.CONTROL_KEY_CHARGES + '-' + i + '-' + j] = chargeControl.errors;
        }
      }
    }

    if (Object.keys(chargeErrors).length) {
      errors[this.CONTROL_KEY_CHARGES] = chargeErrors;
    }

    if (Object.keys(errors).length) {
      return errors;
    } else {
      return null;
    }
  }

  addChargePeriod(chargePeriod?: ChargePeriodScheduleEntry): void {
    const chargesFormArray = new UntypedFormArray([]);
    for (const npppt of this.networkProviderPlanPeriodTypes) {
      let value = null;
      if (chargePeriod) {
        const charge = chargePeriod.charges.find(c => c.period_type.network_provider_plan_period_type_id === npppt.network_provider_plan_period_type_id);
        if (charge) {
          value = charge.charge;
        }
      }
      chargesFormArray.push(new UntypedFormControl(value));
    }

    this.getChargePeriodControls().push(new UntypedFormGroup({
      startMonth: new UntypedFormControl(chargePeriod ? chargePeriod.start_month : null),
      endMonth: new UntypedFormControl(chargePeriod ? chargePeriod.end_month : null),
      charges: chargesFormArray
    }));
  }

  removeChargePeriod(index: number): void {
    this.getChargePeriodControls().removeAt(index);
  }

  publishChange = (formValue: {[key: string]: any}) => {
    const entry: ChargeScheduleEntry = {
      start_ts: this.ngbDateAdapter.toModel(formValue[this.CONTROL_KEY_START_TS]),
      chargePeriods: []
    };

    const chargePeriodControls = this.getChargePeriodControls().controls;
    for (let i = 0; i < chargePeriodControls.length; i++) {
      const chargePeriodFG = <UntypedFormGroup>chargePeriodControls[i];
      const chargePeriod: ChargePeriodScheduleEntry = {
        start_month: chargePeriodFG.get(this.CONTROL_KEY_START_MONTH).value,
        end_month: chargePeriodFG.get(this.CONTROL_KEY_END_MONTH).value,
        charges: []
      };
      entry.chargePeriods.push(chargePeriod);

      const chargeControls = this.getChargeControls(chargePeriodFG).controls;
      for (let j = 0; j < chargeControls.length; j++) {
        const chargeControl = chargeControls[j];
        chargePeriod.charges.push({
          period_type: this.networkProviderPlanPeriodTypes[j],
          charge: chargeControl.value
        });
      }
    }

    this.onChange(entry);
  }

  monthValidationMessageFunction = (value: number, min: number) => {
    return {messageKey: 'Before From Value Error', parameters: {value: this.monthList.find(m => m.id === min).name}};
  }
}
